import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import { formatDateForExport } from '../../../core/utils/exportFormatUtils.ts'

/**
 * One spreadsheet column of a hand-coded grid's export. `value` reads the raw
 * figure, never the formatted cell text: a number stays a number, a date is an
 * ISO date, an id is resolved to its label. `kind` marks the columns the
 * export endpoint types as dates or percentages (a percentage is a fraction,
 * 0.09 for 9%). `when: false` leaves the column out, for columns the grid
 * shows only in some modes.
 */
export interface ExportColumn<T> {
  title: string
  value: (row: T) => unknown
  kind?: 'date' | 'percent'
  when?: boolean
}

/**
 * A business date as YYYY-MM-DD. An ISO string keeps its own calendar date (a
 * timestamptz read in the browser's zone could land on the day before).
 */
function exportDate(value: unknown): unknown {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10)
  return formatDateForExport(value)
}

/** The `/utilities/json-to-excel` payload for `rows`: every column on every row, in column order. */
export function buildTableRowsExcelPayload<T>(rows: readonly T[], columns: ExportColumn<T>[]) {
  const included = columns.filter((c) => c.when !== false)
  const data = rows.map((row) => {
    const out: Record<string, unknown> = {}
    for (const c of included) {
      const v = c.value(row)
      out[c.title] = v === undefined || v === '' ? null : c.kind === 'date' ? exportDate(v) : v
    }
    return out
  })
  return {
    data,
    columns: included.map((c) => c.title),
    dateColumns: included.filter((c) => c.kind === 'date').map((c) => c.title),
    percentageColumns: included.filter((c) => c.kind === 'percent').map((c) => c.title)
  }
}

/**
 * Export a hand-coded grid's rows to Excel as `<name> - <YYYY-MM-DD>.xlsx`.
 * The endpoint refuses an empty sheet, so the caller's button is disabled
 * without rows; reaching here with none says so instead of calling it.
 */
export async function exportTableRows<T>({
  name,
  rows,
  columns
}: {
  name: string
  rows: readonly T[]
  columns: ExportColumn<T>[]
}): Promise<boolean> {
  if (rows.length === 0) {
    toast.info('No rows to export')
    return false
  }
  const filename = `${name} - ${dayjs().format('YYYY-MM-DD')}.xlsx`
  const token = getAccessToken(store.getState().auth)
  const result = await exportJsonToExcel({ ...buildTableRowsExcelPayload(rows, columns), filename }, token)
  if (result.success) {
    toast.success(`${filename} exported successfully`)
    return true
  }
  showErrorToast(result.error || 'Export failed')
  return false
}
